import React, { useEffect, useState } from 'react';  
import axios from 'axios';
import { FaEye, FaEyeSlash, FaBitcoin, FaDatabase, FaUser, FaTachometerAlt, FaCoins, FaChartLine, FaCog } from 'react-icons/fa';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Typography, Collapse, Paper, Pagination, TextField } from '@mui/material';
import { styled } from '@mui/system';
import AdsComponent from './AdsComponent';

const StyledTableCell = styled(TableCell)`
  font-weight: bold;
`;

const Coins = () => {
  const [coins, setCoins] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(8);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    axios.get('/api/currencies')
      .then(response => {
        const data = response.data;
        if (Array.isArray(data)) {
          setCoins(data);
        } else {
          console.error('Invalid data:', data);
        }
      })
      .catch(error => console.error('Error:', error));
  }, []);

  const toggleRow = (index) => {
    if (expandedRows.includes(index)) {
      setExpandedRows(expandedRows.filter(row => row !== index));
    } else {
      setExpandedRows([...expandedRows, index]);
    }
  };

  const getCoinLogo = (symbol) => {
    const sanitizedSymbol = symbol.split(/[-_]/)[0].toUpperCase();
    return `/img/coins/${sanitizedSymbol}.webp`;
  };

  const getPoolLogo = (poolName) => {
    if (!poolName) {
      return '/img/pools/default.webp';
    }
    const poolNameLower = poolName.toLowerCase().replace(/\s+/g, '');
    return `/img/pools/${poolNameLower}.webp`;
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const indexOfLastCoin = currentPage * rowsPerPage;
  const indexOfFirstCoin = indexOfLastCoin - rowsPerPage;

  const filteredCoins = coins.filter(coin =>
    coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coin.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentCoins = filteredCoins.slice(indexOfFirstCoin, indexOfLastCoin);

  return (
    <Box p={3}>
      <Typography variant="h4" component="h2" gutterBottom> 
        <FaCoins /> Coins 
        <a href="http://localhost:3000/NewCoins" style={{ marginLeft: '10px', color: '#007bff', textDecoration: 'none' }}>
          New
        </a>
      </Typography>

      <div className="sidebar-ad">
          <AdsComponent requiredSize="728x90" />
        </div>
      
      {/* Search bar */}
      <Box mb={3}>
        <TextField 
          fullWidth 
          label="Search coin by name or symbol" 
          variant="outlined" 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
        />
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell><FaCog /> Image</StyledTableCell>
              <StyledTableCell><FaChartLine /> Symbol</StyledTableCell>
              <StyledTableCell><FaCoins /> Name</StyledTableCell>
              <StyledTableCell><FaCog /> Algorithm</StyledTableCell>
              <StyledTableCell><FaTachometerAlt /> Total Hashrate</StyledTableCell>
              <StyledTableCell><FaDatabase /> Max Height</StyledTableCell>
              <StyledTableCell>Last Max Block</StyledTableCell>
              <StyledTableCell><FaUser /> Total Workers</StyledTableCell>
              <StyledTableCell>View Pools</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentCoins.map((coin, index) => (
              <React.Fragment key={index}>
                <TableRow>
                  <TableCell>
                    {coin.pools.length > 0 ? (
                      <img 
                        src={getCoinLogo(coin.symbol)} 
                        alt={coin.name} 
                        width="50" 
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }} 
                      />
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>{coin.symbol}</TableCell>
                  <TableCell>{coin.name}</TableCell>
                  <TableCell>{coin.algo}</TableCell>
                  <TableCell>{coin.hashrate || 'N/A'}</TableCell>
                  <TableCell>{coin.height || 'N/A'}</TableCell>
                  <TableCell>{coin.lastblock || 'N/A'}</TableCell>
                  <TableCell>{coin.workers || 'N/A'}</TableCell>
                  <TableCell>
                    <Button variant="contained" color="primary" onClick={() => toggleRow(index)}>
                      {expandedRows.includes(index) ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={expandedRows.includes(index)} timeout="auto" unmountOnExit>
                      <Box margin={2}>
                        <Typography variant="h6" gutterBottom component="div">
                          {coin.name} Pools
                        </Typography>
                        <Table size="small" aria-label="pools">
                          <TableHead>
                            <TableRow>
                              <StyledTableCell>Pools</StyledTableCell>
                              <StyledTableCell>Algorithm</StyledTableCell>
                              <StyledTableCell>Port</StyledTableCell>
                              <StyledTableCell>SSL Port</StyledTableCell>
                              <StyledTableCell><FaTachometerAlt /> Hashrate</StyledTableCell>
                              <StyledTableCell><FaDatabase /> Height</StyledTableCell>
                              <StyledTableCell>Last Block</StyledTableCell>
                              <StyledTableCell><FaUser /> Workers</StyledTableCell>
                              <StyledTableCell>24h Blocks</StyledTableCell>
                              <StyledTableCell><FaBitcoin /> 24h BTC</StyledTableCell>
                              <StyledTableCell>Last Seen</StyledTableCell>
                              <StyledTableCell>Reward</StyledTableCell>
                              <StyledTableCell>Minimum Payout</StyledTableCell>
                              <StyledTableCell>Fee</StyledTableCell>
                              <StyledTableCell>Solo Fee</StyledTableCell>
                              <StyledTableCell>Solo Workers</StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {coin.pools.map((pool, poolIndex) => (
                              <TableRow key={poolIndex}>
                                <TableCell>
                                  <img 
                                    src={getPoolLogo(pool.name)} 
                                    alt={pool.pool_name} 
                                    width="20" 
                                    onError={(e) => { e.target.src = '/img/pools/default.webp'; }} 
                                  />
                                  <a href={pool.url_pool} target="_blank" rel="noopener noreferrer">{pool.name || 'N/A'}</a>
                                </TableCell>
                                <TableCell>{pool.algo || 'N/A'}</TableCell>
                                <TableCell>{pool.port || 'N/A'}</TableCell>
                                <TableCell>{pool.ssl_port || 'N/A'}</TableCell>
                                <TableCell>{pool.hashrate || 'N/A'}</TableCell>
                                <TableCell>{pool.height || 'N/A'}</TableCell>
                                <TableCell>{pool.lastblock || 'N/A'}</TableCell>
                                <TableCell>{pool.workers || 'N/A'}</TableCell>
                                <TableCell>{pool["24h_blocks"] || 'N/A'}</TableCell>
                                <TableCell>{pool["24h_btc"] || 'N/A'}</TableCell>
                                <TableCell>{pool.timesincelast || 'N/A'}</TableCell>
                                <TableCell>{pool.reward || 'N/A'}</TableCell>
                                <TableCell>{pool.minimum_payout || 'N/A'}</TableCell>
                                <TableCell>{pool.fees !== null ? pool.fees : 'N/A'}%</TableCell>
                                <TableCell>{pool.fees_solo || 'N/A'}%</TableCell>
                                <TableCell>{pool.workers_solo || 'N/A'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginator */}
      <Box mt={3} display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(filteredCoins.length / rowsPerPage)}
          page={currentPage}
          onChange={handleChangePage}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default Coins;
